import React, { ReactNode } from "react";
import gql from "graphql-tag";
import { formatDistance } from "date-fns";
import styled from "@emotion/styled";
import { LocationContext } from "@reach/router";
import { BrandCircle } from "@organisms/brandCircleComponents";
import { useStoreAppValue } from "@hooks";
import { prettyUrl, renderIf } from "@services";
import { Box, Image, Text, H2, FontAwesome, TouchableOpacity } from "@atoms";
import { LinkType } from "@types";
import { LinkModalHeroFragment } from "./__generated__/LinkModalHeroFragment";

// == Types ================================================================

interface IProps {
  children: ReactNode;
  link: LinkModalHeroFragment;
  campaignId?: string;
  fundraiserId?: string;
  onLinkClick: () => void;
  locationContext: LocationContext;
}

// == Constants ============================================================

LinkModalHero.fragment = gql`
  fragment LinkModalHeroFragment on Link {
    id
    type
    logoImage
    image
    name
    isBulkShop
    availableDenominations
    giveSentence
    promoSecondaryLabel
    promoEndsAt
    cashbackTiming
    defaultBrand {
      id
      ...BrandCircleFragment
    }
    merchant {
      id
      website
    }
  }
  ${BrandCircle.fragments.fields}
`;

const LOGO_WIDTH = 200;

const DEFAULT_PROPS = {
  selectedFundraisers: [],
};

// == Component ============================================================

function LinkModalHero({ link, locationContext, children }: IProps) {
  const { incentiveAvailable, incentiveAmount, currentUser } = useStoreAppValue();
  const { location, navigate } = locationContext;

  const onBackClick = () => {
    if (location.state.canReturn) {
      window.history.back();
    } else {
      navigate(location.pathname);
    }
  };

  const renderImage = () => {
    if ([LinkType.BRAND, LinkType.TRAVELSHOP].includes(link.type) && link.defaultBrand) {
      return <BrandCircle brand={link.defaultBrand} imageWidth={70} mb={2} mx="auto" pad={14} />;
    }

    if (link.image) {
      return (
        <Image
          alt={link.name}
          borderRadius={isGiftcard ? "giftcard" : 0}
          boxShadow="lightShadow"
          maxWidth={LOGO_WIDTH}
          mb={2}
          mx="auto"
          src={`${link.image}?w=${LOGO_WIDTH}`}
        />
      );
    }
    return null;
  };

  const { merchant, isBulkShop, type: linkType } = link;
  const { GIFTCARDOFFER } = LinkType;
  const bulkShopColorChecker = () => (isBulkShop && !!currentUser ? "purple" : "primary");

  const isGiftcard = linkType === GIFTCARDOFFER;
  return (
    <Box p={3} textAlign="center">
      <Box>
        <TouchableOpacity onTouch={onBackClick}>
          <Text color={bulkShopColorChecker()} fontSize={[3, 4]}>
            <FontAwesome icon="arrow-left" />
          </Text>
        </TouchableOpacity>
      </Box>
      {renderImage()}
      <Box mb={2} textAlign="center">
        <H2 textAlign="center">{link.name}</H2>
        {!isGiftcard && !!merchant && !!merchant.website && (
          <Text display="block" fontSize={0}>
            At {prettyUrl(merchant.website).toLowerCase()}
          </Text>
        )}
        {renderIf(isGiftcard && !!link.availableDenominations)(
          <Text display="block" fontSize={0}>
            {link.availableDenominations}
          </Text>
        )}
      </Box>
      <Box mb={1} textAlign="center">
        <Text
          display="block"
          lineHeight="1"
          textAlign="center"
          textStyle={isBulkShop && !!currentUser ? "bulkShopText" : "givesText"}
        >
          {link.giveSentence}
          {incentiveAvailable &&
            !!incentiveAmount &&
            ` +$${parseFloat(incentiveAmount).toFixed()} First Shop Bonus`}
        </Text>
        {renderIf(!!link.promoSecondaryLabel || !!link.promoEndsAt)(
          <Box textAlign="center">
            {!!link.promoSecondaryLabel && (
              <EPromoLabelText
                as="div"
                color={bulkShopColorChecker()}
                dangerouslySetInnerHTML={{ __html: link.promoSecondaryLabel }}
              />
            )}
            {!!link.promoEndsAt && (
              <Text color={bulkShopColorChecker()} display="block" textStyle="blockText">
                Ends in {formatDistance(new Date(link.promoEndsAt), new Date())}
              </Text>
            )}
          </Box>
        )}
        <Text
          display="block"
          fontSize={0}
          fontWeight="bold"
          mt={1}
          textAlign="center"
        >{`Gives back ${link.cashbackTiming}`}</Text>
      </Box>
      {children}
    </Box>
  );
}

LinkModalHero.defaultProps = DEFAULT_PROPS;

export default LinkModalHero;

// == Styles ===============================================================

const EPromoLabelText = styled(Text)`
  text-align: center;
  color: ${(props) => props.theme.colors.primary};
  font-size: ${(props) => props.theme.fontSizes[0]}px;
  * {
    color: ${(props) => props.theme.colors.primary};
    font-size: ${(props) => props.theme.fontSizes[0]}px;
  }
`;
